import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationError } from "../../models/errors/application-error";
import { ApplicationApprovalStatus } from "../../models/order/application-approval-status";
import { ApplicationLastOrdered } from "../../models/order/application-last-ordered";
import { ApplicationOrder } from "../../models/order/application-order";
import { ApplicationOrderLine } from "../../models/order/application-order-line";
import { ApplicationOrderLineList } from "../../models/order/application-order-line-list";
import { ApplicationOrderLineSplit } from "../../models/order/application-order-line-split";
import { ApplicationOrderList } from "../../models/order/application-order-list";
import { ApplicationApprovalFilter } from "../../models/order/approval/application-approval-filter";
import { ApplicationApprovalOrderFilter } from "../../models/order/approval/application-approval-order-filter";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import { ApplicationSort } from "../../models/shared/application-sort";
import { ApplicationBasketTotals } from "../../models/order/basket/application-basket-totals";
import BaseService from "../base-service";
import IApprovalService from "./approval-service-interface";

export default class ApprovalService extends BaseService implements IApprovalService {
    async getOrders(departmentId: number, currentPosition: number, fetchNext: number, sort: ApplicationSort, filter?: ApplicationApprovalOrderFilter): Promise<ApplicationOrderList> {
        const { data, error } = await this.withRetry(() => client.GET("/v{version}/api/approval/department/{departmentId}", {
            params: {
                path: {
                    version: "1",
                    departmentId
                },
                query: {
                    "Paging.CurrentPosition": currentPosition,
                    "Paging.FetchNext": fetchNext,
                    "Paging.FetchAll": false,
                    Sort: sort,
                },
            },
        }));

        if (!data || error) {
            throw new ApplicationError("Api called failed");
        }

        const mappedResponse = await this.handleResponse(data, "Failed to fetch approvals");
        const orders = mappedResponse.approvals!.map(this.formatOrder);

        return {
            orders,
            count: mappedResponse?.count || 0,
            currentPosition: mappedResponse?.currentPosition || 0,
        };
    }

    async get(orderId: number, currentPosition: number, fetchNext: number, sort: ApplicationSort, sortField: string | undefined, filter?: ApplicationApprovalFilter, searchText?: string): Promise<ApplicationOrderLineList> {
        const { data, error } = await this.withRetry(() => client.GET("/v{version}/api/approval/order/{orderId}", {
            params: {
                path: {
                    version: "1",
                    orderId: orderId
                },
                query: {
                    "Paging.CurrentPosition": currentPosition,
                    "Paging.FetchNext": fetchNext,
                    "Paging.FetchAll": false,
                    Sort: sort,
                    SortField: sortField,
                    SearchText: searchText,
                    "Filter.PositiveQuantity": filter?.positiveQuantity,
                    "Filter.SupplierId": filter?.supplierId,
                    "Filter.ZeroQuantity": filter?.zeroQuantity,
                    "Filter.ApprovalStatus": filter?.approvalStatus
                },
            },
        }));

        if (!data || error) {
            throw new ApplicationError("Api called failed");
        }

        const mappedResponse = await this.handleResponse(data, "Failed to fetch approvals");
        const orderLines = mappedResponse.approvals!.map(this.mapOrderLine);

        return {
            orderLines,
            count: mappedResponse?.count || 0,
            currentPosition: mappedResponse?.currentPosition || 0,
        };
    }


    async getById(orderLineId: number): Promise<ApplicationOrderLine> {
        const { data, error } = await this.withRetry(() => client.GET(
            "/v{version}/api/approval/orderline/{orderLineId}",
            {
                params: {
                    path: { version: "1", orderLineId },
                },
            }
        ));

        if (!data || error) {
            throw new ApplicationError("Api call failed fetching order line");
        }

        const response = await this.handleResponse(data, "Failed to fetch order");
        return this.mapOrderLine(response ?? {});
    }

    async updateApproval(orderLine: ApplicationOrderLine): Promise<ApplicationOrderLine> {
        const { data, error } = await this.withRetry(() => client.POST("/v{version}/api/approval", {
            params: {
                path: { version: "1" },  // Path parameter                
            },
            body: {
                ...orderLine,
                lastOrdered: {}
            }
        }));

        if (!data || error) {
            throw new ApplicationError("Update approval api called failed");
        }

        var mappedResponse = await this.handleResponse(data, "Failed to fetch approval");
        return this.mapOrderLine(mappedResponse);
    }

    async updateStatus(orderId: number, status: ApplicationApprovalStatus, filters?: ApplicationApprovalFilter): Promise<void> {
        const { error } = await this.withRetry(() => client.POST("/v{version}/api/approval/order/{orderId}", {
            params: {
                path: { version: "1", orderId },
            },
            body: {
                status: status,
                filter: filters
            }
        }));

        if (error) {
            throw new ApplicationError("Update approve all api called failed");
        }
    }

    async count(orderId: number, status: ApplicationApprovalStatus): Promise<number> {
        const { error, data } = await this.withRetry(() => client.GET("/v{version}/api/approval/order/{orderId}/{status}/count", {
            params: {
                path: { version: "1", orderId, status },
            }
        }));

        if (error) {
            throw new ApplicationError("Update approve all api called failed");
        }

        return data.data ?? 0;
    }

    async getTotals(orderId: number): Promise<ApplicationBasketTotals> {
        const { data, error } = await this.withRetry(() => client.GET(
            "/v{version}/api/order/{orderId}/totals",
            {
                params: {
                    path: { version: "1", orderId },
                },
            }
        ));

        if (!data?.success || !data.data || error) {
            throw new ApplicationError("Failed to fetch order totals");
        }

        return {
            totalItemCount: data.data.totalItemCount ?? 0,
            totalValue: data.data.totalValue ?? 0,
            suppliers: data.data.suppliers?.map((supplier) => ({
                supplierId: supplier.supplierId ?? 0,
                supplierName: supplier.supplierName ?? "Unknown supplier",
                itemCount: supplier.itemCount ?? 0,
                totalValue: supplier.totalValue ?? 0,
                currentTotalPalletEquivalent: supplier.currentTotalPalletEquivalent,
                requiredMinimumPallets: supplier.requiredMinimumPallets,
                palletShortfall: supplier.palletShortfall,
                isPalletMinimumMet: supplier.isPalletMinimumMet,
            })) ?? [],
        };
    }

    private async handleResponse<T>(response: { success?: boolean | undefined, data?: T | undefined }, errorMessage: string): Promise<T> {
        if (!response.success) {
            throw new ApplicationError(errorMessage);
        }
        return response.data!;
    }

    private formatOrder(order: any): ApplicationOrder {
        return {
            id: order.id,
            branchId: order.branchId,
            lineCount: order.lineCount,
            displayName: `Order #${order.id}`,
            created: order.created,
            status: order.status
        };
    }
    private mapOrderLine = (orderLine: components["schemas"]["OrderLineDTO"]): ApplicationOrderLine => {
        return {
            id: orderLine.id ?? 0,
            orderId: orderLine.orderId ?? 0,
            productId: orderLine.productId ?? 0,
            productName: orderLine.productName ?? "",
            productDescription: orderLine.productDescription ?? "",
            productImage: orderLine.productImage ?? "",
            size: orderLine.size ?? 0,
            sizeUnit: mapStringToEnum(ApplicationSizeUnit, orderLine.sizeUnit ?? ""),
            quantity: orderLine.quantity ?? 0,
            currentStockLevel: orderLine?.currentStockLevel ?? 0,
            costPrice: orderLine?.costPrice ?? 0,
            openPricing: orderLine?.openPricing ?? false,
            notes: orderLine.notes ?? "",
            plu: orderLine.plu ?? "",
            supplierId: orderLine?.supplierId ?? 0,
            supplierName: orderLine.supplierName ?? "",
            approvalStatus: mapStringToEnum(
                ApplicationApprovalStatus,
                orderLine.approvalStatus ?? ""
            ),
            children: orderLine?.splits?.map(x => this.mapOrderLineSplits(x)) ?? [],
            lastOrdered: orderLine.lastOrdered ? this.mapLastOrdered(orderLine.lastOrdered!) : undefined
        };
    }

    private mapOrderLineSplits(split: components["schemas"]["OrderLineSplitDTO"]): ApplicationOrderLineSplit {
        return {
            id: split?.orderLineId ?? 0,
            orderLineId: split?.orderLineId ?? 0,
            supplierId: split?.supplierId ?? 0,
            supplierName: split?.supplierName ?? "",
            size: split?.size ?? 0,
            sizeUnit: mapStringToEnum(ApplicationSizeUnit, split.sizeUnit ?? ""),
            costPrice: split?.costPrice ?? 0,
            quantity: split?.quantity ?? 0,
            notes: split?.notes ?? "",
        };
    }

    private mapLastOrdered(lastOrdered: components["schemas"]["LastOrderedDTO"]): ApplicationLastOrdered {
        return {
            date: new Date(lastOrdered.date!),
            quantity: lastOrdered.quantity!,
            cost: lastOrdered.cost!,
            unitCost: lastOrdered.unitCost!,
            currentStock: lastOrdered.currentStock! 
        }
    }
}